import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Star, Clock, Award, ArrowRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useAnimations";
import { AnimatedCounter } from "./AnimatedCounter";

const BUSINESS_PHONE_LINK = "tel:+33549057490";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2010016e-c6ae-4f83-80db-30e5f9c887c1/artifacts/7vmwauk8_LOGO%20MEDIASTORE.JPG";
const SHOP_URL = "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg";

export const HeroSection = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);

  return (
    <section className="relative hero-bg py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container-custom">
        <div
          ref={heroRef}
          className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${
            heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Left Content */}
          <div className="space-y-8">
            <Badge className="badge-open px-4 py-2 text-sm font-medium animate-pulse">
              <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-ping"></span>
              Ouvert aujourd&apos;hui jusqu&apos;à 18h
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              Réparation Express
              <span className="block text-gradient-animate">Téléphone, Tablette & Ordinateur</span>
              à Niort
            </h1>

            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Apple
              </div>
              <div className="inline-flex items-center gap-2 bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                <Award className="w-5 h-5" />
                Techniciens Certifiés
              </div>
            </div>

            <p className="text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
              <strong className="text-red-600">Techniciens certifiés Apple</strong> depuis plus de 10 ans.
              iPhone, iPad, MacBook, iMac + Samsung, Huawei, Xiaomi...
              <strong> Diagnostic gratuit</strong>, devis transparent.{" "}
              <strong className="text-red-600">4,6★ sur Google</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BUSINESS_PHONE_LINK}>
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 h-14 px-8 text-lg font-semibold btn-glow gap-2 ripple"
                  data-testid="hero-call-btn"
                >
                  <Phone className="h-5 w-5" />
                  05 49 05 74 90
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto h-14 px-8 text-lg font-semibold border-2 gap-2 scale-hover"
                  data-testid="hero-quote-btn"
                >
                  Demander un devis
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>

            {/* Trust Signals Mobile */}
            <div className="grid grid-cols-2 gap-4 lg:hidden">
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border card-lift">
                <Star className="h-8 w-8 text-red-600" />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">4,6/5 sur Google</p>
                  <p className="text-xs text-slate-500">212 avis clients</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border card-lift">
                <Clock className="h-8 w-8 text-red-600" />
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Réparation express</p>
                  <p className="text-xs text-slate-500">Dès 30 minutes</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Logo officiel + Photo boutique */}
          <div className="relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl float bg-gradient-to-br from-red-50 to-white">
              <div className="flex items-center justify-center pt-10 pb-6 bg-white">
                <img
                  src={LOGO_URL}
                  alt="Logo Mediastore - Spécialiste Apple Certifié à Niort"
                  className="w-48 h-48 rounded-full object-cover shadow-lg ring-4 ring-red-100"
                  data-testid="hero-logo"
                  loading="eager"
                  width="192"
                  height="192"
                />
              </div>

              <div className="relative">
                <img
                  src={SHOP_URL}
                  alt="Boutique Mediastore Niort - 104 Rue de la Gare, intérieur atelier réparation Apple"
                  className="w-full h-[260px] object-cover"
                  loading="lazy"
                  width="600"
                  height="260"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>

                <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-4 bounce-in" style={{ animationDelay: "0.5s" }}>
                  <div className="flex items-center gap-4">
                    <div className="flex -space-x-2 shrink-0">
                      <div className="w-10 h-10 rounded-full bg-red-100 border-2 border-white flex items-center justify-center text-sm font-bold text-red-600">M</div>
                      <div className="w-10 h-10 rounded-full bg-green-100 border-2 border-white flex items-center justify-center text-sm font-bold text-green-600">S</div>
                      <div className="w-10 h-10 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center text-sm font-bold text-purple-600">P</div>
                      <div className="w-10 h-10 rounded-full bg-orange-100 border-2 border-white flex items-center justify-center text-sm font-bold text-orange-600">L</div>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">
                        +<AnimatedCounter end={200} /> clients satisfaits ce mois
                      </p>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm text-slate-600 ml-1 font-medium">4,6/5</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
